import { Injectable } from '@angular/core';
import { getKeyFromModel, uriNameOfEntityOrEmpty } from '@briebug/ngrx-auto-entity';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { from } from 'rxjs';
import { filter, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { ListOption } from '../../../shared/popovers/types/list-options';
import { userEntities } from '../../users/user/user.state';
import { WorkOrder } from '../work-order/work-order.model';
import { loadWorkOrder } from '../work-order/work-order.state';
import { KudosUIService } from './kudos-ui.service';
import { kudosGiven } from './kudos.actions';
import { Kudos, KudosReason } from './kudos.model';
import { createKudos, createKudosSuccess } from './kudos.state';

@Injectable()
export class KudosEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly kudosUI: KudosUIService,
    private readonly store: Store,
  ) { }

  giveKudos$ = createEffect(() =>
    this.actions$.pipe(
      ofType(kudosGiven),
      withLatestFrom(this.store.select(userEntities)),
      switchMap(([{ workOrder }, users]) =>
        from(this.kudosUI.promptKudosReason(users[workOrder.awardedToInstallerId])).pipe(
          filter(({ role, data }) => !!data),
          map(({ data }) => ({ workOrder, kudosData: data || [] })),
        ),
      ),
      map(({ workOrder, kudosData }) => {
        const kudos = this.convertListOptionsToKudos(workOrder.id!, kudosData);

        return createKudos({
          entity: kudos as any,
          criteria: {
            parents: {
              [uriNameOfEntityOrEmpty(WorkOrder)]: getKeyFromModel(WorkOrder, workOrder),
            },
          },
        });
      }),
    ),
  );

  private convertListOptionsToKudos(workOrderId: string, options: ListOption[]): Omit<Kudos, 'id'> {
    const kudos: any = {
      workOrderId,
      reasons: [],
    };

    const fieldMapping: { [key: string]: keyof Kudos } = {
      [KudosReason.Professionalism]: 'professionalism',
      [KudosReason.Dependability]: 'dependability',
      [KudosReason.Punctuality]: 'punctuality',
      [KudosReason.Quality]: 'quality',
      [KudosReason.Attitude]: 'attitude',
      [KudosReason.TimelyCommunication]: 'timelyCommunication',
      [KudosReason.LikelyToUseAgain]: 'likelyToUseAgain',
    };

    options.forEach((option) => {
      if (option.name && option.value !== undefined) {
        kudos.reasons.push(option.name as KudosReason);


        const fieldName = fieldMapping[option.name];
        if (fieldName) {
          kudos[fieldName] = option.value;
        }
      }
    });

    return kudos as Omit<Kudos, 'id'>;
  }

  reloadWorkOrderOnKudosGiven$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createKudosSuccess),
      map(({ entity }) => loadWorkOrder({ keys: entity.workOrderId })),
    ),
  );
}
